import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/query-client';
import type { RdtEstadoDerivado, TipoAdjunto, TipoRdt } from '@/types/api';

// Mutations de DetalleObra (Tramo 2, backend/src/routes/rdts.ts). Patrón
// único de invalidación (pedido explícito del encargo): CUALQUIER mutación
// que toca una RDT invalida ['rdt', id] (la ficha/Actividad abiertas),
// ['obra-rdts', cc] (la card de la lista — período, estado, conteo de
// comentarios, etc.) y ['obras'] (la home: el badge/toggle «A chequear (N)»
// depende de chequeos/hitos/altas/borrados/archivados de RDTs y quedaba stale
// hasta 5 min si no se invalidaba acá) — sin optimistic UI: la app es
// interna, invalidate + refetch alcanza. También invalida ['avivador'] (build
// Avivador, 21/9/2026, design_avivador.md §10.4): cualquier chequeo, monto o
// hito completado/deshecho puede sacar o meter la rede en una rama distinta.
function invalidarRdt(queryClient: QueryClient, cc: string, id: number | null): void {
  void queryClient.invalidateQueries({ queryKey: ['obra-rdts', cc] });
  if (id !== null) void queryClient.invalidateQueries({ queryKey: ['rdt', id] });
  void queryClient.invalidateQueries({ queryKey: ['obras'] });
  void queryClient.invalidateQueries({ queryKey: ['avivador'] });
}

interface OkResponse {
  ok: true;
}

// -----------------------------------------------------------------------
// Alta / edición / borrado / archivado
// -----------------------------------------------------------------------

// Alta mínima ("todo es por actividad" — Juan 2026-07-11): crear una RDT
// pide SOLO nro + tipo + responsable. Referencia y %/monto entran después
// por pasos (Expediente / Chequeo de índices / Monto informado); póliza
// entra como checkbox del paso "Aprobado" (resolución #8 del panel).
export interface CrearRdtInput {
  cc: string;
  nro: string;
  tipo: TipoRdt;
  responsableId?: number | null;
}

interface CrearRdtResponse extends OkResponse {
  id: number;
  // Nro guardado, YA normalizado (decisión de Juan 2026-09-14, ver
  // backend/src/domain/rdt-nro.ts) — opcional por si un backend viejo
  // todavía no lo manda; los callers caen a normalizarNro(nroTipeado).
  nro?: string;
  estado: RdtEstadoDerivado;
}

// POST /api/rdts — el `id` de la respuesta lo usa la página para seleccionar
// la RDT recién creada.
export function useCrearRdt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearRdtInput) => api<CrearRdtResponse>('/api/rdts', { method: 'POST', body: input }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, null),
  });
}

export interface ActualizarRdtHitosInput {
  entrega?: 'entregado_obra' | 'entregado_cliente' | null;
  polizaAplica?: boolean;
  fechas?: Record<string, string | null>;
}

// Identidad (nro/responsable) ahora se edita INLINE en la ficha (ajuste 2 de
// Juan) y los hitos se corrigen en el form reducido de edicion-rdt.tsx.
// `tipo` (Provisoria↔Definitiva) también se edita ahí, pero SOLO si la RDT
// no tiene chequeos ni montos cargados — el backend guarda esa condición y
// devuelve 422 si no se cumple (punto 3 v0.9.0, eco en el hilo vía
// rdt_eventos campo='tipo'); si ya se cargó mal con datos: borrado soft +
// recrear. `nota` (punto 4/12) queda en rdt_eventos junto al eco de
// identidad. Referencia/%/monto siguen sin pasar por este PUT, salen todos
// de pasos.
export interface ActualizarRdtInput {
  cc: string;
  id: number;
  nro?: string;
  responsableId?: number | null;
  tipo?: TipoRdt;
  nota?: string | null;
  // Motivo de la corrección (0.12.0, QA ronda 2, Juan 2026-09-15): nombre que
  // usa el frontend desde la separación nota-del-paso / motivo-de-la-
  // corrección — si viene junto con `nota`, el backend prioriza `motivo`.
  // `nota` se mantiene por compatibilidad, este PUT no la usa más.
  motivo?: string;
  hitos?: ActualizarRdtHitosInput;
}

// PUT /api/rdts/:id — edición parcial de campos core + hitos (entrega XOR,
// póliza, fechas encadenadas). 422 con mensaje legible en cronología/secuencia
// inválida — se propaga tal cual vía ApiError para el toast del caller.
export function useActualizarRdt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...patch }: ActualizarRdtInput) =>
      api<{ ok: true; estado: RdtEstadoDerivado }>(`/api/rdts/${id}`, { method: 'PUT', body: patch }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// DELETE /api/rdts/:id — soft delete (la actividad queda huérfana-soft con la RDT).
export function useBorrarRdt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { cc: string; id: number }) =>
      api<OkResponse & { conteos: Record<string, number> }>(`/api/rdts/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// POST /api/rdts/:id/archivar y /desarchivar — `nota` opcional (punto 4/12
// v0.9.0), visible en el eco del hilo (rdt_eventos campo='archivada').
export function useArchivarRdt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archivar, nota }: { cc: string; id: number; archivar: boolean; nota?: string | null }) =>
      api<OkResponse>(`/api/rdts/${id}/${archivar ? 'archivar' : 'desarchivar'}`, {
        method: 'POST',
        ...(nota !== undefined ? { body: { nota } } : {}),
      }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Chequeos e hitos
// -----------------------------------------------------------------------

export interface CrearChequeoInput {
  cc: string;
  id: number;
  periodo: string;
  valorPct?: number | null;
  salta: boolean;
  fechaIndices?: string | null;
  nota?: string | null;
}

// POST /api/rdts/:id/chequeos — 409 si el período ya tiene chequeo, 422 si la
// RDT ya saltó (el ciclo sigue en una RDT nueva); ambos con .error legible.
export function useCrearChequeo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...body }: CrearChequeoInput) =>
      api<{ ok: true; id: number; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/chequeos`, {
        method: 'POST',
        body,
      }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// POST /api/rdts/:id/hitos/completar — 422 con mensaje legible si falta la
// entrega previa o la fecha rompe la cronología. `polizaAplica` solo aplica
// con codigo='aprobado' (checkbox "requiere póliza EC" del paso Aprobado —
// resolución #8 del panel adversarial: reemplaza el toggle que antes vivía
// en el form de alta/edición). `nota` opcional (punto 4/12 v0.9.0) queda en
// rdt_hitos.nota, visible en el ítem del hilo.
export function useCompletarHito() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cc: _cc,
      id,
      codigo,
      fecha,
      polizaAplica,
      nota,
    }: {
      cc: string;
      id: number;
      codigo: string;
      fecha: string;
      polizaAplica?: boolean;
      nota?: string | null;
    }) =>
      api<{ ok: true; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/hitos/completar`, {
        method: 'POST',
        body: { codigo, fecha, ...(polizaAplica !== undefined ? { polizaAplica } : {}), ...(nota !== undefined ? { nota } : {}) },
      }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// POST /api/rdts/:id/hitos/deshacer — deshace el ÚLTIMO hito completado
// (orden inverso estricto sobre el catálogo; 422 con mensaje legible si hay
// uno posterior sin deshacer todavía). Affordance nueva sobre el
// des-completar que el PUT con fecha null ya permitía (punto 11a v0.9.0) —
// eco en el hilo vía rdt_eventos campo='hito', `nota` opcional.
export interface DeshacerHitoInput {
  cc: string;
  id: number;
  codigo: string;
  nota?: string | null;
}

export function useDeshacerHito() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...body }: DeshacerHitoInput) =>
      api<{ ok: true; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/hitos/deshacer`, { method: 'POST', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Montos (paso "Monto informado", solo Definitivas) — last-wins por fecha
// de negocio, desempate creado_at/id (resolución #3 del panel adversarial).
// -----------------------------------------------------------------------

// `monto`/`valorPct` opcionales, al menos uno (Definitivas de SOFSE/ADIF se
// gestionan por FRI/% en vez de monto — decisión de Juan, QA 2026-09-14): el
// 400 «Informá el monto, el porcentaje o los dos» lo valida el backend, acá
// solo se espeja el contrato.
export interface CrearMontoInput {
  cc: string;
  id: number;
  monto?: number | null;
  valorPct?: number | null;
  periodo: string;
  fecha: string;
  nota?: string | null;
}

export function useCrearMonto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...body }: CrearMontoInput) =>
      api<{ ok: true; id: number; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/montos`, { method: 'POST', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export interface EditarMontoInput {
  cc: string;
  id: number;
  mid: number;
  monto?: number | null;
  valorPct?: number | null;
  periodo?: string;
  fecha?: string;
  nota?: string | null;
  // Motivo de la corrección (0.12.0, QA ronda 2, Juan 2026-09-15): separado de
  // `nota` (que sigue siendo la nota DEL PASO) — viaja solo como rdt_eventos.nota
  // de los ecos que este PATCH genere, nunca pisa `rdt_montos.nota`.
  motivo?: string;
}

// PATCH — corrige un monto YA cargado in-place (marca "editado"), nunca
// cargar otro paso con fecha vieja para arreglar un valor mal tipeado.
export function useEditarMonto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, mid, ...body }: EditarMontoInput) =>
      // `sinCambios` (0.12.0, QA ronda 2 — hallazgo 6): un PATCH que no cambia
      // nada no escribe UPDATE/eco/auditoría, el backend lo marca así en vez
      // de tratarlo como una edición real; opcional porque el caller (acá el
      // "cinturón" `if (!hayCambios)` de MontoEditForm) ya evita mandarlo casi
      // siempre, pero un doble-click en Guardar puede llegar a pedirlo igual.
      api<{ ok: true; estado: RdtEstadoDerivado; sinCambios?: boolean }>(`/api/rdts/${id}/montos/${mid}`, { method: 'PATCH', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useBorrarMonto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mid }: { cc: string; id: number; mid: number }) =>
      api<{ ok: true; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/montos/${mid}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Referencias (paso "Expediente", ambos tipos) — last-wins por (creado_at, id).
// -----------------------------------------------------------------------

export interface CrearReferenciaInput {
  cc: string;
  id: number;
  referencia: string;
  nota?: string | null;
}

export function useCrearReferencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...body }: CrearReferenciaInput) =>
      api<{ ok: true; id: number; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/referencias`, { method: 'POST', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export interface EditarReferenciaInput {
  cc: string;
  id: number;
  rid: number;
  referencia?: string;
  nota?: string | null;
  /** Nota del acto (v0.14.0): motivo del eco de corrección (rdt_eventos.nota), nunca pisa la nota del paso. */
  motivo?: string;
}

export function useEditarReferencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, rid, ...body }: EditarReferenciaInput) =>
      // `sinCambios` (v0.14.0): mismo contrato no-op que montos/chequeos.
      api<{ ok: true; estado: RdtEstadoDerivado; sinCambios?: boolean }>(`/api/rdts/${id}/referencias/${rid}`, { method: 'PATCH', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useBorrarReferencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rid }: { cc: string; id: number; rid: number }) =>
      api<{ ok: true; estado: RdtEstadoDerivado }>(`/api/rdts/${id}/referencias/${rid}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Edición de un chequeo YA registrado — alcance limitado (resolución #7):
// valorPct/nota/fechaIndices; `salta` es siempre inmutable (backend lo
// rechaza si viene en el body). `periodo` (R3, Juan 2026-09-14) es la
// EXCEPCIÓN: editable SOLO en chequeos migrados del histórico
// (creado_por_id/autor null) — el backend lo valida server-side, acá solo
// se manda si chequeos-tabla.tsx lo sumó al body (migrado + cambiado).
// -----------------------------------------------------------------------

export interface EditarChequeoInput {
  cc: string;
  id: number;
  chid: number;
  valorPct?: number | null;
  nota?: string | null;
  fechaIndices?: string | null;
  periodo?: string;
  // Motivo de la corrección (0.12.0, QA ronda 2, Juan 2026-09-15): separado de
  // `nota` (que sigue siendo la nota DEL PASO) — viaja solo como rdt_eventos.nota
  // de los ecos que este PATCH genere, nunca pisa `rdt_chequeos.nota`.
  motivo?: string;
}

export function useEditarChequeo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, chid, ...body }: EditarChequeoInput) =>
      // `sinCambios` (0.12.0, QA ronda 2 — hallazgo 6): idem useEditarMonto,
      // ver comentario ahí.
      api<{ ok: true; estado: RdtEstadoDerivado; sinCambios?: boolean }>(`/api/rdts/${id}/chequeos/${chid}`, { method: 'PATCH', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Seguimientos (contactos)
// -----------------------------------------------------------------------

export interface CrearSeguimientoInput {
  cc: string;
  id: number;
  interlocutor: string;
  resultado: string;
  fecha: string;
  nota?: string | null;
}

export function useCrearSeguimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc: _cc, id, ...body }: CrearSeguimientoInput) =>
      api<{ ok: true; id: number }>(`/api/rdts/${id}/seguimientos`, { method: 'POST', body }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useBorrarSeguimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sid }: { cc: string; id: number; sid: number }) =>
      api<OkResponse>(`/api/rdts/${id}/seguimientos/${sid}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Comentarios
// -----------------------------------------------------------------------

export function useCrearComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, texto }: { cc: string; id: number; texto: string }) =>
      api<{ ok: true; id: number }>(`/api/rdts/${id}/comentarios`, { method: 'POST', body: { texto } }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useActualizarComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cid, texto }: { cc: string; id: number; cid: number; texto: string }) =>
      api<OkResponse>(`/api/rdts/${id}/comentarios/${cid}`, { method: 'PUT', body: { texto } }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useBorrarComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cid }: { cc: string; id: number; cid: number }) =>
      api<OkResponse>(`/api/rdts/${id}/comentarios/${cid}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

// -----------------------------------------------------------------------
// Adjuntos — multipart/form-data: lib/api.ts es JSON-only (fuerza
// Content-Type: application/json), así que acá va un fetch propio, mismo
// contrato de error { error: string } que lib/api.ts (patrón de
// hooks/use-clientes.ts subirLogoRequest, pero con FormData en vez de un
// blob crudo: multer.single('archivo') espera un campo de formulario).
// -----------------------------------------------------------------------

export interface SubirAdjuntoInput {
  cc: string;
  id: number;
  tipo: TipoAdjunto;
  archivo: File;
  nota?: string | null;
}

interface AdjuntoCreadoResponse {
  ok: true;
  id: number;
  tipo: TipoAdjunto;
  nombre: string;
  mime: string;
  tamanoBytes: number;
  nota: string | null;
}

async function subirAdjuntoRequest(input: SubirAdjuntoInput): Promise<AdjuntoCreadoResponse> {
  const formData = new FormData();
  formData.append('tipo', input.tipo);
  if (input.nota) formData.append('nota', input.nota);
  formData.append('archivo', input.archivo);

  const res = await fetch(`/api/rdts/${input.id}/adjuntos`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // sin body JSON: se pierde en silencio, no es crítico para el manejo de errores
    }
    const message =
      parsedBody && typeof parsedBody === 'object' && 'error' in parsedBody
        ? String((parsedBody as { error: unknown }).error)
        : `POST /api/rdts/${input.id}/adjuntos → HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsedBody);
  }

  return (await res.json()) as AdjuntoCreadoResponse;
}

export function useSubirAdjunto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: subirAdjuntoRequest,
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

export function useBorrarAdjunto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, aid }: { cc: string; id: number; aid: number }) =>
      api<OkResponse>(`/api/rdts/${id}/adjuntos/${aid}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidarRdt(queryClient, variables.cc, variables.id),
  });
}

/** href de descarga de un adjunto — GET simple, la cookie de sesión viaja
 *  sola en la navegación same-origin (no hace falta un hook de query). */
export function adjuntoHref(id: number, aid: number): string {
  return `/api/rdts/${id}/adjuntos/${aid}`;
}
